import React, { useState } from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useNavigate } from "@/lib/router-compat";
import { postTrackingEvent, buildTrackingPayload } from "@/lib/tracking";
import { trackLead } from "@/lib/ga4";
import { trackLeadConversion } from "@/lib/conversions";
import { PRISM_BOOKING_URL } from "./prismData";

/**
 * Section C — Self-reflection checklist.
 * Placed directly after Section A.
 *
 * Interactive checkboxes are for the visitor's own reflection. When they
 * submit the name + phone form to learn more, the statements they checked
 * are carried into the CRM as a custom field so the care team can see what
 * resonated. A "Print this checklist" link opens a print-friendly version
 * of the card in a new window.
 */
const statements = [
  "I get pulled back into memories or feelings I didn't choose to think about",
  "I go out of my way to avoid certain places, people or topics",
  "I keep myself busy so I don't have to feel things",
  "I feel numb or distant from the people closest to me",
  "I carry guilt or shame that doesn't seem to ease",
  "I'm on guard or easily startled, even in safe places",
  "My sleep is broken or restless",
  "Things I used to enjoy don't do much for me anymore",
  "Getting started on everyday tasks feels unusually hard",
  "I'm getting through the days without really feeling present in them",
];

export default function SelfReflectionChecklist() {
  const navigate = useNavigate();
  const [checked, setChecked] = useState<boolean[]>(() =>
    statements.map(() => false),
  );
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const toggle = (i: number) =>
    setChecked((prev) => prev.map((v, idx) => (idx === i ? !v : v)));

  const printChecklist = () => {
    const win = window.open("", "_blank", "noopener,noreferrer");
    if (!win) return;
    const items = statements
      .map((s) => `<li><span class="box">&#9744;</span> ${s}</li>`)
      .join("");
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8" />
      <title>Prism self-reflection checklist — Neuro Wellness TMS</title>
      <style>
        body{font-family:Georgia,'Times New Roman',serif;color:#102A43;max-width:640px;margin:40px auto;padding:0 24px;line-height:1.5;}
        h1{font-size:22px;margin:0 0 4px;}
        .sub{color:#5B7A99;font-size:14px;margin:0 0 6px;}
        .intro{font-size:15px;margin:0 0 20px;}
        ul{list-style:none;padding:0;margin:0 0 20px;}
        li{display:flex;gap:10px;padding:8px 0;border-bottom:1px solid #E2E8F0;font-size:15px;}
        .box{font-size:18px;line-height:1.2;}
        .foot{font-size:12px;color:#5B7A99;margin-top:24px;}
      </style></head><body>
      <h1>Could this be what I'm dealing with?</h1>
      <p class="sub">A quick self-reflection</p>
      <p class="intro">In the past month, how many of these sound like you? This isn't a test or a diagnosis. It's a way to notice patterns worth bringing to a clinician.</p>
      <ul>${items}</ul>
      <p class="foot">This checklist is for personal reflection only and can't diagnose PTSD or depression. Only a qualified clinician can make a diagnosis. If you're in crisis, call or text 988 or call 911.</p>
      </body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Collect the statements the visitor checked so the care team can see
    // what resonated — carried through the registered custom field.
    const checkedStatements = statements
      .filter((_, i) => checked[i])
      .map((s) => `- ${s}`)
      .join("\n");
    const reflectionSummary =
      checkedStatements ||
      "(No statements checked — visitor submitted without selecting any.)";

    const payload = buildTrackingPayload(
      "prism-self-reflection",
      "Prism Self-Reflection — Learn More",
    );
    payload.formData = {
      first_name: firstName,
      last_name: lastName,
      phone,
    };
    payload.formLabels = {
      first_name: "First name",
      last_name: "Last name",
      phone: "Phone",
    };
    postTrackingEvent(payload, {
      customFields: {
        HF2VQFvBRBD2r4e5qS5O: {
          value: reflectionSummary,
          label: "Prism Self-Reflection Checklist",
        },
      },
    });
    trackLead("prism-self-reflection", "Prism Self-Reflection — Learn More");
    trackLeadConversion(
      "prism-self-reflection",
      "Prism Self-Reflection — Learn More",
    );
    setSubmitted(true);
  };

  return (
    <section
      className="relative bg-gradient-hero py-16 lg:py-24"
      aria-labelledby="prism-checklist-heading"
    >
      <div className="container-px mx-auto max-w-3xl">
        <div className="rounded-2xl border-2 border-accent/30 bg-card p-6 shadow-premium sm:p-8">
          <h2
            id="prism-checklist-heading"
            className="font-headings text-2xl font-bold text-primary sm:text-3xl"
          >
            Could this be what I&rsquo;m dealing with?
          </h2>
          <p className="mt-1 text-sm font-semibold uppercase tracking-widest text-medium-blue">
            A quick self-reflection
          </p>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            In the past month, how many of these sound like you? This
            isn&rsquo;t a test or a diagnosis. It&rsquo;s a way to notice
            patterns worth bringing to a clinician.
          </p>

          <ul className="mt-6 space-y-2.5">
            {statements.map((s, i) => (
              <li key={s}>
                <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-border bg-background px-4 py-3 transition-colors hover:border-accent/40">
                  <input
                    type="checkbox"
                    checked={checked[i]}
                    onChange={() => toggle(i)}
                    className="mt-0.5 h-5 w-5 flex-shrink-0 rounded border-border text-accent accent-[var(--accent)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
                  />
                  <span className="text-[15px] leading-relaxed text-foreground">
                    {s}
                  </span>
                </label>
              </li>
            ))}
          </ul>

          <p className="mt-6 text-base leading-relaxed text-muted-foreground">
            If several of these felt familiar, you&rsquo;re not imagining it and
            you&rsquo;re not alone. A consultation with our team is a
            low-pressure way to sort out what&rsquo;s going on and what might
            help, whether that&rsquo;s Prism or something else.
          </p>

          {/* ── Learn-more form: name + phone, carries checked statements ── */}
          {submitted ? (
            <div
              className="mt-8 rounded-xl border border-accent/40 bg-accent/10 p-6 text-center"
              role="status"
              aria-live="polite"
            >
              <CheckCircle2 className="mx-auto h-8 w-8 text-accent" />
              <h3 className="mt-3 font-headings text-lg font-semibold text-primary">
                Thanks &mdash; we&rsquo;ll be in touch.
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Our care team will reach out within one business day to talk
                through what you&rsquo;ve noticed. For anything urgent, call or
                text{" "}
                <a
                  href="tel:9548279793"
                  className="font-semibold text-accent underline underline-offset-4"
                >
                  954-827-9793
                </a>
                .
              </p>
              <div className="mt-5">
                <Button
                  asChild
                  className="bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90"
                >
                  <a href={PRISM_BOOKING_URL}>Book a consultation</a>
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-8 space-y-4">
              <p className="text-sm font-semibold text-primary">
                Want us to reach out? Leave your name and number.
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="prism-reflection-first">First name *</Label>
                  <Input
                    id="prism-reflection-first"
                    required
                    autoComplete="given-name"
                    placeholder="Jane"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="prism-reflection-last">Last name *</Label>
                  <Input
                    id="prism-reflection-last"
                    required
                    autoComplete="family-name"
                    placeholder="Doe"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="prism-reflection-phone">Phone *</Label>
                <Input
                  id="prism-reflection-phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  required
                  placeholder="(555) 123-4567"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <Button
                  type="submit"
                  size="lg"
                  className="bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90"
                >
                  Send me more information
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <button
                  type="button"
                  onClick={printChecklist}
                  className="text-sm font-semibold text-accent underline underline-offset-4 hover:opacity-80"
                >
                  Print this checklist
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                We&rsquo;ll only use your details to follow up about Prism. Your
                information is private and never shared.
              </p>
            </form>
          )}

          <p className="mt-5 text-xs leading-relaxed text-muted-foreground">
            This checklist is for personal reflection only and can&rsquo;t
            diagnose PTSD or depression. Only a qualified clinician can make a
            diagnosis. If you&rsquo;re in crisis, call or text{" "}
            <a
              href="tel:988"
              className="font-semibold text-accent underline underline-offset-4"
            >
              988
            </a>{" "}
            or call{" "}
            <a
              href="tel:911"
              className="font-semibold text-accent underline underline-offset-4"
            >
              911
            </a>
            .
          </p>
        </div>
      </div>
    </section>
  );
}
